import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { memo, useCallback, useRef, useState } from 'react';
import { T, useT } from '../../../translation/src';
import { AI_IMAGE_TYPES, pastedImageFile, prepareReferenceImage } from './aiShared';

export interface ReferenceImageState {
  /** Data URL ready to send, or null. */
  image: string | null;
  /** The last picked/pasted file could not be used. */
  unreadable: boolean;
  /** A file is being read/downscaled. */
  reading: boolean;
  pick: (file: File) => Promise<void>;
  clear: () => void;
  /** For the description field: an image pasted into it becomes the reference. */
  onPaste: (event: React.ClipboardEvent) => void;
}

/** State of an optional reference image (file picker + paste). */
export function useReferenceImage(): ReferenceImageState {
  const [image, setImage] = useState<string | null>(null);
  const [unreadable, setUnreadable] = useState(false);
  const [reading, setReading] = useState(false);

  const pick = useCallback(async (file: File) => {
    setReading(true);
    const prepared = await prepareReferenceImage(file);
    setReading(false);
    setUnreadable(prepared === null);
    if (prepared) setImage(prepared);
  }, []);

  const clear = useCallback(() => {
    setImage(null);
    setUnreadable(false);
  }, []);

  const onPaste = useCallback(
    (event: React.ClipboardEvent) => {
      const file = pastedImageFile(event.clipboardData);
      if (!file) return;
      event.preventDefault();
      void pick(file);
    },
    [pick],
  );

  return { image, unreadable, reading, pick, clear, onPaste };
}

interface ReferenceImageFieldProps {
  state: ReferenceImageState;
  disabled?: boolean;
}

/** Thumbnail + remove, or a button to pick one (with a paste hint). */
const ReferenceImageField_: React.FC<ReferenceImageFieldProps> = ({ state, disabled }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const removeLabel = useT('remove image', { swc: true });
  const previewAlt = useT('reference image', { swc: true });

  return (
    <div className="flex flex-col gap-1">
      {state.image ? (
        <div className="flex items-center gap-2">
          <img src={state.image} alt={previewAlt} className="h-16 max-w-[8rem] rounded-md border border-gray-300 bg-white object-contain dark:border-gray-600" />
          <button
            type="button"
            onClick={state.clear}
            disabled={disabled}
            aria-label={removeLabel}
            title={removeLabel}
            className="flex h-7 w-7 items-center justify-center rounded-md text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-40 dark:text-gray-300 dark:hover:bg-white/10"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || state.reading}
            className={classNames(
              'flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40',
              'dark:border-gray-600 dark:hover:bg-black',
            )}
          >
            <PhotoIcon className="h-4 w-4" />
            <T _str="add reference image" swc />
          </button>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            <T _str="optional: a sketch, photo or plan symbol (or paste one)" swc />
          </span>
        </div>
      )}
      {state.unreadable && (
        <span role="alert" className="text-xs text-error-red-500">
          <T _str="that image could not be used: pick a PNG, JPEG, WebP or GIF" swc />
        </span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={AI_IMAGE_TYPES.join(',')}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void state.pick(file);
        }}
      />
    </div>
  );
};

export const ReferenceImageField = memo(ReferenceImageField_);
